import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

export const BuyerForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    companyName: '', contactName: '', contactEmail: '', contactPhone: '',
    address: '', city: '', country: '', category: 'roaster', notes: '',
  });
  const [relatedData, setRelatedData] = useState<{ contracts: any[]; exportInvoices: any[] }>({ contracts: [], exportInvoices: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetch(`/api/admin/buyers?id=${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setForm({
            companyName: data.companyName || '',
            contactName: data.contactName || '',
            contactEmail: data.contactEmail || '',
            contactPhone: data.contactPhone || '',
            address: data.address || '',
            city: data.city || '',
            country: data.country || '',
            category: data.category || 'roaster',
            notes: data.notes || '',
          });
          setRelatedData({
            contracts: data.contracts || [],
            exportInvoices: data.exportInvoices || [],
          });
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    if (!form.companyName.trim()) {
      setError('Company name is required');
      setSaving(false);
      return;
    }

    const body = isEdit ? { id, ...form } : form;
    try {
      const res = await fetch('/api/admin/buyers', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
      navigate('/admin/buyers');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Buyer' : 'New Buyer'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{isEdit ? 'Update client details' : 'Add a new client to the CRM'}</p>
        </div>
        <button onClick={() => navigate('/admin/buyers')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to buyers
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Details */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Company Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Company Name *</label>
              <input name="companyName" value={form.companyName} onChange={handleChange} required placeholder="Keynote Coffee Limited" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select name="category" value={form.category} onChange={handleChange} className={inputClass}>
                <option value="roaster">Roaster</option>
                <option value="importer">Importer</option>
                <option value="trader">Trader</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Address</label>
              <input name="address" value={form.address} onChange={handleChange} placeholder="Cable House, 5th Floor 60 New Broad Street" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>City</label>
              <input name="city" value={form.city} onChange={handleChange} placeholder="London" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Country</label>
              <input name="country" value={form.country} onChange={handleChange} placeholder="United Kingdom" className={inputClass} />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Primary Contact</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Contact Name</label>
              <input name="contactName" value={form.contactName} onChange={handleChange} placeholder="Lola Dobney" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input name="contactEmail" type="email" value={form.contactEmail} onChange={handleChange} placeholder="lola@keynote.coffee" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="+44 7730797357" className={inputClass} />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Internal notes about this client..." className={inputClass} />
        </div>

        {/* Related data (edit mode only) */}
        {isEdit && (relatedData.contracts.length > 0 || relatedData.exportInvoices.length > 0) && (
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
            <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Related Records</h2>
            {relatedData.contracts.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold mb-2">Contracts ({relatedData.contracts.length})</p>
                <div className="space-y-1.5">
                  {relatedData.contracts.map((c: any) => (
                    <Link key={c.id} to={`/admin/contracts/${c.id}`} className="block text-sm text-[#1A1A1A] hover:text-[#F05A1E] font-medium transition-colors">
                      {c.contractNumber} — {c.coffeeDescription || 'No description'} ({c.status})
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {relatedData.exportInvoices.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold mb-2">Export Invoices ({relatedData.exportInvoices.length})</p>
                <div className="space-y-1.5">
                  {relatedData.exportInvoices.map((inv: any) => (
                    <Link key={inv.id} to={`/admin/export-invoices/${inv.id}`} className="block text-sm text-[#1A1A1A] hover:text-[#F05A1E] font-medium transition-colors">
                      {inv.invoiceNumber} — ${parseFloat(inv.totalAmount || 0).toLocaleString()} ({inv.status})
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Buyer' : 'Create Buyer'}
          </button>
          <button type="button" onClick={() => navigate('/admin/buyers')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
